/**
 * Fixed navigation bar with transparent-to-blurred background on scroll.
 * Desktop: horizontal text links with underline hover effect.
 * Mobile: full-screen overlay drawer triggered by a hamburger icon.
 * On the homepage the logo is hidden; on sub-pages it links back to home.
 */
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "@/assets/thursdayjam-logo.svg";

const navItems = [
  { label: "Music", href: "#vinyl-player", isRoute: false },
  { label: "Videos", href: "/videos", isRoute: true },
  { label: "Gallery", href: "/gallery", isRoute: true },
  { label: "We Love", href: "/music-we-love", isRoute: true },
  { label: "About", href: "/about", isRoute: true },
];

interface NavigationProps {
  variant?: "default" | "about";
}

let hasAnimated = false;

const Navigation = ({ variant = "default" }: NavigationProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [heroLogoHidden, setHeroLogoHidden] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuAnimated = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isFirstMount = !hasAnimated;

  useEffect(() => {
    hasAnimated = true;
  }, []);

  const isHomePage = location.pathname === "/";
  // Check if a nav item is active
  const isActive = (href: string, isRoute: boolean) => {
    if (isRoute) {
      return location.pathname === href;
    }
    // For anchor links on homepage, they're "active" when on homepage
    return isHomePage && !isRoute;
  };

  // Display all nav items on all pages, converting anchors to routes when not on homepage
  const displayNavItems = navItems.map(item => ({
    ...item,
    // Convert anchor links to route + anchor for non-homepage
    href: !isHomePage && !item.isRoute ? `/${item.href}` : item.href,
    isRoute: !isHomePage ? true : item.isRoute,
  }));

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
      if (isHomePage) {
        setHeroLogoHidden(window.scrollY > window.innerHeight * 0.55);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  const handleNavClick = (href: string, isRoute: boolean) => {
    setIsMobileMenuOpen(false);
    mobileMenuAnimated.current = false;

    // Cross-page hash link (e.g. "/#vinyl-player" from a sub-page)
    if (href.includes("#") && isRoute) {
      const hash = href.split("#")[1];
      navigate("/");
      // Wait for homepage to render, then scroll to anchor
      setTimeout(() => {
        const el = document.getElementById(hash);
        el?.scrollIntoView({ behavior: "smooth" });
      }, 300);
      return;
    }

    if (isRoute) {
      navigate(href);
    } else {
      // Same-page anchor — smooth scroll
      const element = document.querySelector(href);
      element?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleLogoClick = () => {
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <motion.nav
        initial={isFirstMount ? { y: -100, opacity: 0 } : false}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 md:top-4 md:left-4 md:right-4 z-[100] ${
          isScrolled
            ? "bg-background/80 backdrop-blur-xl border-b border-border"
            : "bg-transparent"
        }`}
        style={{
          transition: "background-color 500ms ease, border-color 500ms ease, backdrop-filter 500ms ease"
        }}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between pl-6 pr-6 md:pl-12 md:pr-12 py-6 md:py-8">
          {/* Logo - always visible on sub-pages, fades in/out on homepage based on hero logo visibility */}
          <AnimatePresence>
            {(!isHomePage || heroLogoHidden) && (
              <motion.a
                key="nav-logo"
                href="/"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                onClick={(e) => { e.preventDefault(); handleLogoClick(); }}
                className="flex items-center"
              >
                <img
                  src={logo}
                  alt="Thursday Jam"
                  className="h-10 md:h-12 w-auto transition-transform duration-300 ease-out hover:scale-105"
                />
              </motion.a>
            )}
          </AnimatePresence>
          {isHomePage && !heroLogoHidden && <div />}

          {/* Desktop Navigation - staggered fly-in on first mount */}
          <div className="hidden items-center md:flex">
            {displayNavItems.map((item, index) => {
              const active = isActive(item.href, item.isRoute);
              return item.isRoute ? (
                <motion.a
                  key={item.label}
                  href={item.href}
                  initial={isFirstMount ? { opacity: 0, y: -20 } : false}
                  animate={{ opacity: 1, y: 0 }}
                  transition={isFirstMount ? { duration: 0.6, delay: 0.3 + index * 0.1, ease: [0.16, 1, 0.3, 1] } : undefined}
                  onClick={(e) => { e.preventDefault(); handleNavClick(item.href, item.isRoute); }}
                  className={`group relative px-6 py-4 font-display text-xs uppercase tracking-[0.2em] transition-colors ${
                    active ? "text-foreground" : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  {item.label}
                  <span className={`absolute bottom-3 left-6 right-6 h-px bg-foreground transition-all duration-300 ${
                    active ? "w-[calc(100%-3rem)]" : "w-0 group-hover:w-[calc(100%-3rem)]"
                  }`} />
                </motion.a>
              ) : (
                <motion.button
                  key={item.label}
                  initial={isFirstMount ? { opacity: 0, y: -20 } : false}
                  animate={{ opacity: 1, y: 0 }}
                  transition={isFirstMount ? { duration: 0.6, delay: 0.3 + index * 0.1, ease: [0.16, 1, 0.3, 1] } : undefined}
                  onClick={() => handleNavClick(item.href, item.isRoute)}
                  className={`group relative px-6 py-4 font-display text-xs uppercase tracking-[0.2em] transition-colors ${
                    active ? "text-foreground" : "text-foreground/70 hover:text-foreground"
                  }`}
                >
                  {item.label}
                  <span className={`absolute bottom-3 left-6 right-6 h-px bg-foreground transition-all duration-300 ${
                    active ? "w-[calc(100%-3rem)]" : "w-0 group-hover:w-[calc(100%-3rem)]"
                  }`} />
                </motion.button>
              );
            })}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-foreground md:hidden"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.nav
            id="mobile-nav"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col items-center justify-center bg-background md:hidden"
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col items-center gap-12">
              {displayNavItems.map((item, index) => item.isRoute ? (
                <motion.a
                  key={item.label}
                  href={item.href}
                  initial={mobileMenuAnimated.current ? false : { opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ delay: index * 0.1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  onAnimationComplete={() => {
                    if (index === displayNavItems.length - 1) {
                      mobileMenuAnimated.current = true;
                    }
                  }}
                  onClick={(e) => { e.preventDefault(); handleNavClick(item.href, item.isRoute); }}
                  className="font-display text-4xl tracking-tight text-foreground transition-opacity hover:opacity-50"
                >
                  {item.label}
                </motion.a>
              ) : (
                <motion.button
                  key={item.label}
                  initial={mobileMenuAnimated.current ? false : { opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ delay: index * 0.1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  onAnimationComplete={() => {
                    if (index === displayNavItems.length - 1) {
                      mobileMenuAnimated.current = true;
                    }
                  }}
                  onClick={() => handleNavClick(item.href, item.isRoute)}
                  className="font-display text-4xl tracking-tight text-foreground transition-opacity hover:opacity-50"
                >
                  {item.label}
                </motion.button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
